import React, { useState } from 'react';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Button } from 'design-system/Form';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
// TODO: Migrate module so that you don’t import from the corp project in a global module
/* eslint-disable restrict-imports/restrict-imports */
import { TextInput, Text } from 'design-system';
import { requiredAsterisk as defaultAsterisk } from 'react-scale/corp/helpers';

const description = `
### Description:
- This tool allows you to upload eagle batches
- Select the spreadsheet ID and sheet name
- Make sure your sheet has the following columns: project, radarid, text, waws, samples
- Click on Upload button

##### Author: Carlos Gonzalez
---
`;

const defaultSpreadsheetURL =
  'https://docs.google.com/spreadsheets/d/1rpohSx_cnIj15PHJOts2po4hrcsA29P3XMu3QJruP2s';

// eslint-disable-next-line complexity
const UploadEagleBatches = () => {
  const [spreadsheetURL, setSpreadsheetURL] = useState<string>(defaultSpreadsheetURL);
  const [sheet, setSheet] = useState<string>('');
  const [inputError, setInputError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();
  const enabled = !!spreadsheetURL && !!sheet;

  const handleUpload = async () => {
    setLoading(true);
    setMessage('');
    // eslint-disable-next-line no-console
    console.log(
      `Uploading eagle batches for spreadsheet ${defaultSpreadsheetURL} and sheet ${sheet}`,
    );

    try {
      const res = await apiFetch(null, 'corp-api/bee-tts-utils/uploadEagleBatches', {
        method: 'POST',
        body: {
          spreadsheetURL,
          sheet,
        },
      });

      setSpreadsheetURL(defaultSpreadsheetURL);
      setSheet('');
      setLoading(false);
      setInputError('');
      setMessage(res.message);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      setLoading(false);
    }
  };

  const buttonText = () => {
    if (!spreadsheetURL || !!inputError) return 'Input spreadsheet URL';
    if (!sheet) return 'Input sheet name';
    return 'Upload Eagle Batches';
  };

  const renderOutput = () => {
    return (
      <div className="response-markdown">
        <SanitizedMarkdown text={message || ''} />
        <style jsx global>{`
          .response-markdown tr {
            border: 1px solid rgba(0, 0, 0, 0.3);
          }

          .response-markdown tr:nth-child(even) {
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgb(0, 0, 0, 0.3);
            padding: 6px 10px;
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 6px 10px;
            background-color: #222;
            color: white;
          }

          .response-markdown td {
            border: 1px solid rgba(0, 0, 0, 0.3);
            padding: 10px;
          }
        `}</style>
      </div>
    );
  };

  const onChangeSpreadsheetURL = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSpreadsheetURL(event.target.value.trim());
  };

  const onChangeSheet = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSheet(event.target.value.trim());
  };

  return (
    <div className="root">
      <section>
        <SanitizedMarkdown text={description} />
      </section>
      <section className="container">
        <div className="textTitle">
          <Text medium>Spreadsheet URL</Text> {defaultAsterisk}
        </div>
        <div className="file-validation">
          <div className="input-component">
            <TextInput value={spreadsheetURL} onChange={onChangeSpreadsheetURL} fullWidth />
            <small className="hint">
              Please make sure you shared the sheet with
              `doordash-google-drive@scaleapi.iam.gserviceaccount.com`
            </small>
          </div>
        </div>
      </section>
      <section className={spreadsheetURL ? 'container' : 'container disabled'}>
        <div className="textTitle">
          <Text medium>Sheet Name</Text> {defaultAsterisk}
        </div>
        <div className="file-validation">
          <div className="input-component">
            <TextInput
              value={sheet}
              onChange={onChangeSheet}
              disabled={!spreadsheetURL}
              fullWidth
            />
            <small className="hint">
              NOTE: It will ONLY process rows with row.action === "upload"
            </small>
          </div>
        </div>
      </section>
      <br />
      <section>
        <Button
          primary
          disabled={!enabled || !spreadsheetURL || !!inputError || !sheet || loading}
          requireConfirmation
          onClick={handleUpload}
        >
          {buttonText()}
        </Button>
        {message && renderOutput()}
      </section>
      <style jsx>{`
        .container {
          margin: 32px 0;
          width: 75%;
        }
        .hint {
          font-size: 13px;
          font-weight: normal;
        }
        .disabled {
          color: var(--color-RemoGray50);
        }
        .file-validation {
          display: flex;
          justify-content: space-between;
        }
        .input-component {
          width: 75%;
        }
      `}</style>
    </div>
  );
};

export default UploadEagleBatches;
